import { Button, Col, Row } from "react-bootstrap"
import ManualAccessibilityAuditBlock from "../ManualAccessibilityAuditBlock"
import { useTranslation } from "react-i18next";

import "../../Assets/scss/components/normal-dashboard.scss";
import { StoreProptypes } from "../../PropTypes/StoreProptypes";
import { useSelector } from "react-redux";
import React, { useEffect, useState } from "react";
import ViolationsTableData from "./ViolationsTable";
import { generatePDFService } from "../../Services/DashboardPDFReportService";
import InitMsgBlock from "../InitMsgBlock";
import FreeVersionOverlay from "../FreeVersionOverlay";
import ExpiredVersionOverlay from "../ExpiredVersionOverlay";
import ScanningAndMonitoringBlock from "../ScanningAndMonitoringBlock";
import CancelVersionOverlay from "../CancelVersionOverlay";

// component to render normal user dashboard
const NormalUserDashboardBlock = () => {

    const { t } = useTranslation();

    // Redux Data
    const { accessToken, activeLanguage, currentWebsite, currentWebsiteData, currentWebsitePlanStatus } = useSelector((store: StoreProptypes) => store);

    // state
    const [violationsType, setViolationsType] = useState<number>(1);
    const [isViolationsTypeButton, setIsViolationsTypeButton] = useState<boolean>(false);
    const [reportPDFLink, setReportPDFLink] = useState<string>('');
    const [loadingPDF, setLoadingPDF] = useState<boolean>(true);


    useEffect(() => {

        // check if its a pro plan or if its a trial plan
        if ((currentWebsitePlanStatus.isProPlan || currentWebsitePlanStatus.isTrial)) {
            // check access token ,current website and active language
            if (accessToken && currentWebsite && activeLanguage) {
                // service to generate pdf
                generatePDFService(accessToken, currentWebsite.id, activeLanguage.code, setLoadingPDF, setReportPDFLink);
            }
        }

        // check if plan is expired or if its a free widget
        if (currentWebsitePlanStatus.isExpired || currentWebsitePlanStatus.isFreeWidget) {
            setViolationsType(0);
            setIsViolationsTypeButton(true);

        } else {
            setViolationsType(1);
            setIsViolationsTypeButton(false);
        }

    }, [currentWebsite])

    // function to handle PDF download
    const handlePDFDownload = () => {
        // check if report pdf link available
        if (reportPDFLink) {
            window.open(reportPDFLink, '_blank');
        }
    }

    return (
        <>
            <Row>
                <Col className="col-12 col-xxxl-12 col-xxxxl-9">
                    <div className={`aioa_dashboard-violations-info-wrapper ${(currentWebsitePlanStatus.isInit || currentWebsitePlanStatus.isFreeWidget || currentWebsitePlanStatus.isExpired || currentWebsitePlanStatus.isCanceledPlan) ? `website-init-msg` : ``}`}>

                        {(currentWebsitePlanStatus.isFreeWidget) ? (
                            <>
                                <FreeVersionOverlay />
                            </>
                        ) :
                            <>
                                {currentWebsitePlanStatus.isInit && (
                                    <>
                                        <InitMsgBlock />
                                    </>
                                )}
                            </>

                        }

                        {(currentWebsitePlanStatus.isExpired) ? (
                            <>
                                <ExpiredVersionOverlay />
                            </>
                        ) :
                            <></>

                        }
                         {currentWebsite?.canceled_package_only && currentWebsite?.canceled_package_only.length > 0 ? (
                            <>
                                <CancelVersionOverlay />
                            </>
                        ) :
                            <></>

                        }

                        <div className="aioa_dashboard-violations-graph">
                            <Row xs={1} md={3} className="gy-4 gy-md-0">
                                <Col>
                                    <div className="aioa_dashboard-violations-graph-box">
                                        <h2 className="aioa_dashboard-violations-graph-box-title h5">{t('accessibility score label')}
                                            <Button variant="link" href={`https://www.skynettechnologies.com/automated-accessibility-score-limitations`}
                                                    className="info-btn" aria-label={t("Learn more about Accessibility score").toString()} target={`_blank`}>
                                            <i className="material-symbols-outlined" aria-hidden="true">info</i>
                                        </Button></h2>
                                        <div className="aioa_dashboard-violations-semi-donut-graph" style={{ "--percentage": currentWebsiteData.accessibilityScore, "--fill": '#CADAEA' } as React.CSSProperties}>
                                            <div className="aioa_dashboard-violations-graph-box-per h2">{Math.min(currentWebsiteData.accessibilityScore, 99)}%*</div>
                                            <div className={`aioa_dashboard-violations-graph-box-info h6 mb-0 bold-text 
                                                ${currentWebsiteData.accessibilityScore > 85 ? `text-success success` : currentWebsiteData.accessibilityScore > 49 ? `text-warning warning` : `text-danger danger`}`}>
                                                {currentWebsiteData.accessibilityScore > 85 ? t("Compliant") : currentWebsiteData.accessibilityScore > 49 ? t("Semi Compliant") : t("Not Compliant")}
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                                <Col>
                                    <div className="aioa_dashboard-violations-graph-box">
                                        <h2 className="aioa_dashboard-violations-graph-box-title h5">{t('violations remediated label')}</h2>
                                        <div className="aioa_dashboard-violations-semi-donut-graph" style={{ "--percentage": currentWebsiteData.violationsRemediatedPercentage, "--fill": '#CADAEA' } as React.CSSProperties}>
                                            <div className="aioa_dashboard-violations-graph-box-per h2">{currentWebsiteData.violationsRemediatedPercentage}%</div>
                                            <div className="aioa_dashboard-violations-graph-box-info h6 mb-0">{t('out of out', { firstNumber: currentWebsiteData.successViolations, lastNumber: (currentWebsiteData.failedViolations + currentWebsiteData.successViolations) })}</div>
                                        </div>
                                    </div>
                                </Col>
                                <Col>
                                    <div className="aioa_dashboard-violations-graph-box">
                                        <h2 className="aioa_dashboard-violations-graph-box-title h5">{t('alt text remediated label')}</h2>
                                        <div className="aioa_dashboard-violations-semi-donut-graph" style={{ "--percentage": currentWebsiteData.imageAltRemediatedPercentage, "--fill": '#CADAEA' } as React.CSSProperties}>
                                            <div className="aioa_dashboard-violations-graph-box-per h2">{currentWebsiteData.imageAltRemediatedPercentage}%</div>
                                            <div className="aioa_dashboard-violations-graph-box-info h6 mb-0">{t('out of out', { firstNumber: currentWebsiteData.successImageAlt, lastNumber: (currentWebsiteData.successImageAlt + currentWebsiteData.failedImageAlt) })}</div>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </div>

                        <div className="aioa_dashboard-violations-list mt-4">

                            <div className="aioa_dashboard-violations-list-title-wrapper">
                                <div className="aioa_dashboard-violations-list-title h5 mb-0">{t('accessibility violations section title')}</div>
                                <div className="aioa_dashboard-violations-list-tab">
                                    <ul>
                                        <li><Button className={`${violationsType === 1 ? `active` : ``}`} size="sm" onClick={() => setViolationsType(1)} disabled={isViolationsTypeButton}>{t('violations remediated label')}</Button></li>
                                        <li><Button className={`${violationsType === 0 ? `active` : ``}`} size="sm" onClick={() => setViolationsType(0)}>{t('violations manual action tab label')}</Button></li>
                                    </ul>
                                </div>
                            </div>

                            {violationsType === 1 ? (
                                <>
                                    <ViolationsTableData violationsList={currentWebsiteData.successViolationsList} violationsType={1} />
                                </>
                            ) : (
                                <>
                                    <ViolationsTableData violationsList={currentWebsiteData.failedViolationsList} violationsType={0} />
                                </>
                            )}

                        </div>
                    </div>
                    {(currentWebsitePlanStatus.isProPlan || currentWebsitePlanStatus.isTrial) && (!currentWebsitePlanStatus.isInit) && (
                        <>
                            <div className="aioa_dashboard-violations-report-wrapper">
                                <Button size="lg" disabled={loadingPDF} onClick={() => handlePDFDownload()}>{t('get pdf report label')}</Button>
                            </div>
                        </>
                    )}
                </Col>
                <Col className="col-12 col-xxxl-12 col-xxxxl-3">
                    <div className="dashboard-banners-wrapper">
                        <ManualAccessibilityAuditBlock />
                        <ScanningAndMonitoringBlock />
                    </div>
                </Col>
            </Row>
        </>
    )
}

export default NormalUserDashboardBlock;
